import { format, isValid, startOfDay } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { useEffect, useId, useMemo, useState } from "react";
import { strings } from "@/I18n";
import { cn } from "@/lib/utils";
import { Button } from "@/v2/ui/primitives/button";
import { Calendar } from "@/v2/ui/primitives/calendar";
import { Input } from "@/v2/ui/primitives/input";
import { Label } from "@/v2/ui/primitives/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/v2/ui/primitives/popover";

interface DateTimePickerProps {
  id?: string;
  value?: Date | string | null;
  onChange?: (date: Date | null) => void;
  label?: string;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  maxDate?: Date;
  minDate?: Date;
}

export function DateTimePicker({
  id,
  value,
  onChange,
  label,
  placeholder = strings("common.pickDateTime"),
  className,
  disabled = false,
  maxDate,
  minDate,
}: DateTimePickerProps) {
  const [open, setOpen] = useState(false);
  const generatedId = useId();
  const inputId = id ?? generatedId;

  const dateValue = useMemo(() => {
    if (!value) return null;
    if (value instanceof Date) return isValid(value) ? value : null;
    const parsed = new Date(value);
    return isValid(parsed) ? parsed : null;
  }, [value]);

  const [hours, setHours] = useState(() =>
    dateValue ? dateValue.getHours().toString().padStart(2, "0") : "00"
  );
  const [minutes, setMinutes] = useState(() =>
    dateValue ? dateValue.getMinutes().toString().padStart(2, "0") : "00"
  );

  useEffect(() => {
    if (dateValue) {
      setHours(dateValue.getHours().toString().padStart(2, "0"));
      setMinutes(dateValue.getMinutes().toString().padStart(2, "0"));
    }
  }, [dateValue]);

  const commitDate = (date: Date) => {
    // Calendar bounds are days; the committed value must respect the full instant.
    const boundedDate =
      minDate && date < minDate
        ? new Date(minDate)
        : maxDate && date > maxDate
          ? new Date(maxDate)
          : date;
    setHours(boundedDate.getHours().toString().padStart(2, "0"));
    setMinutes(boundedDate.getMinutes().toString().padStart(2, "0"));
    onChange?.(boundedDate);
  };

  const handleDateSelect = (selectedDate: Date | undefined) => {
    if (!selectedDate) {
      onChange?.(null);
      return;
    }

    const newDate = new Date(selectedDate);
    newDate.setHours(parseInt(hours, 10) || 0);
    newDate.setMinutes(parseInt(minutes, 10) || 0);
    newDate.setSeconds(0);
    newDate.setMilliseconds(0);
    commitDate(newDate);
  };

  const handleTimeChange = (type: "hours" | "minutes", val: string) => {
    const numericValue = val.replace(/\D/g, "");

    if (type === "hours") {
      const h = Math.min(23, Math.max(0, parseInt(numericValue, 10) || 0));
      setHours(
        numericValue.length <= 2 ? numericValue : h.toString().padStart(2, "0")
      );
    } else {
      const m = Math.min(59, Math.max(0, parseInt(numericValue, 10) || 0));
      setMinutes(
        numericValue.length <= 2 ? numericValue : m.toString().padStart(2, "0")
      );
    }
  };

  const handleTimeBlur = () => {
    let h = parseInt(hours, 10) || 0;
    let m = parseInt(minutes, 10) || 0;

    h = Math.min(23, Math.max(0, h));
    m = Math.min(59, Math.max(0, m));

    setHours(h.toString().padStart(2, "0"));
    setMinutes(m.toString().padStart(2, "0"));

    if (dateValue) {
      const newDate = new Date(dateValue);
      newDate.setHours(h);
      newDate.setMinutes(m);
      newDate.setSeconds(0);
      newDate.setMilliseconds(0);
      commitDate(newDate);
    }
  };

  const formattedValue = dateValue
    ? format(dateValue, "dd/MM/yyyy HH:mm")
    : null;

  return (
    <div className={cn("tw-flex tw-flex-col tw-gap-1.5", className)}>
      {label && (
        <Label htmlFor={inputId} className="tw-text-sm tw-font-medium">
          {label}
        </Label>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild={true}>
          <Button
            id={inputId}
            variant="outline"
            className={cn(
              "tw-w-[220px] tw-justify-start tw-text-left tw-font-normal tw-h-14",
              !dateValue && "tw-text-muted-foreground"
            )}
            disabled={disabled}
          >
            <CalendarIcon className="tw-mr-2 tw-h-4 tw-w-4" />
            {formattedValue || <span>{placeholder}</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="tw-w-auto tw-p-0"
          align="start"
          collisionPadding={12}
          style={{ maxWidth: "calc(100vw - 24px)" }}
        >
          <Calendar
            mode="single"
            selected={dateValue || undefined}
            onSelect={handleDateSelect}
            disabled={(date) => {
              if (maxDate && date > startOfDay(maxDate)) return true;
              if (minDate && date < startOfDay(minDate)) return true;
              return false;
            }}
            initialFocus={true}
          />
          <div className="tw-border-t tw-p-3">
            <div className="tw-flex tw-items-center tw-gap-2">
              <Label className="tw-text-sm tw-text-muted-foreground">
                {strings("common.time")}
              </Label>
              <div className="tw-flex tw-items-center tw-gap-1">
                <Input
                  type="text"
                  inputMode="numeric"
                  aria-label={strings("common.hours")}
                  value={hours}
                  onChange={(e) => handleTimeChange("hours", e.target.value)}
                  onBlur={() => handleTimeBlur()}
                  className="tw-w-12 tw-text-center tw-h-9"
                  style={{ width: "3rem", minWidth: 0, flexShrink: 0 }}
                  maxLength={2}
                  placeholder="00"
                />
                <span className="tw-text-muted-foreground tw-font-medium">
                  :
                </span>
                <Input
                  type="text"
                  inputMode="numeric"
                  aria-label={strings("common.minutes")}
                  value={minutes}
                  onChange={(e) => handleTimeChange("minutes", e.target.value)}
                  onBlur={() => handleTimeBlur()}
                  className="tw-w-12 tw-text-center tw-h-9"
                  style={{ width: "3rem", minWidth: 0, flexShrink: 0 }}
                  maxLength={2}
                  placeholder="00"
                />
              </div>
              {/*
                The time inputs share this popover with the calendar, so
                picking a date cannot close it. Without an explicit way out the
                popover keeps covering whatever sits below the field -- the
                confirm button of the dialog it is in, typically.
              */}
              <Button
                type="button"
                size="sm"
                className="tw-ml-auto"
                onClick={() => setOpen(false)}
              >
                {strings("common.done")}
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
